import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import "../styles/recipeDetail.css";
import fallbackImage from "../assets/images/torta.jpg";

export default function RecipeDetail() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorScreen, setErrorScreen] = useState(false);

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from("recipes")
        .select("*, recipe_images(*)")
        .eq("id", id)
        .single();

      if (error || !data) {
        console.error("Greška pri učitavanju recepta:", error);
        setErrorScreen(true);
        setLoading(false);
        return;
      }

      // Razdvoji slike po sekcijama
      if (data.recipe_images && Array.isArray(data.recipe_images) && data.recipe_images.length > 0) {
        console.log("All recipe_images:", data.recipe_images);
        console.log("Recipe images with sections:", data.recipe_images.map(img => ({ id: img.id, section: img.section })));
        
        // Glavne slike (bez section ili null)
        data.mainImages = data.recipe_images.filter(img => {
          const section = img.section;
          return !section || section === null || section === "";
        });
        
        // Slike za sastojke
        data.ingredientsImages = data.recipe_images.filter(img => {
          const section = String(img.section || "").toLowerCase().trim();
          return section === "ingredients";
        });
        
        // Slike za korake pripreme
        data.stepsImages = data.recipe_images.filter(img => {
          const section = String(img.section || "").toLowerCase().trim();
          return section === "steps";
        });
        
        console.log("Main images:", data.mainImages.length);
        console.log("Ingredients images:", data.ingredientsImages.length);
        console.log("Steps images:", data.stepsImages.length);
        console.log("Steps images data:", data.stepsImages);
        
        // Sortiraj glavne slike po redoslijedu ako postoji order polje
        if (data.mainImages.length > 0) {
          try {
            data.mainImages.sort((a, b) => {
              if (a && b && 'order' in a && 'order' in b) {
                const orderA = a.order !== null && a.order !== undefined ? a.order : (a.id || 0);
                const orderB = b.order !== null && b.order !== undefined ? b.order : (b.id || 0);
                return orderA - orderB;
              }
              return (a?.id || 0) - (b?.id || 0);
            });
          } catch (sortError) {
            console.warn("Greška pri sortiranju slika:", sortError);
          }
        }
      } else {
        data.mainImages = [];
        data.ingredientsImages = [];
        data.stepsImages = [];
      }

      setRecipe(data);
      setLoading(false);
    }

    load();
  }, [id]);

  if (loading)
    return <p className="loading">Učitavanje recepta…</p>;

  if (errorScreen)
    return (
      <div className="error-message">
        <h2>Ups! Nešto je pošlo po zlu</h2>
        <p>Nije moguće učitati recept. Molimo pokušajte ponovo.</p>
        <Link to="/" className="btn-back">← Vrati se na početnu</Link>
      </div>
    );

  if (!recipe) return null;

  // Razdvojene slike po sekcijama
  const mainImages = recipe.mainImages || [];
  const ingredientsImages = recipe.ingredientsImages || [];
  const stepsImages = recipe.stepsImages || [];

  return (
    <div className="recipe-detail">
      <div className="recipe-header">
        <h1>{recipe.title}</h1>
      </div>

      <p className="recipe-short">{recipe.description}</p>

      {/* Glavne fotografije (bez section) */}
      {mainImages.length > 0 && (
        <div className="recipe-images">
          {mainImages.map((img, index) => (
            <div key={img.id || index} className="recipe-image-wrapper">
              <img
                src={img.image_url}
                alt={recipe.title}
                className="recipe-image"
              />
            </div>
          ))}
        </div>
      )}

      <div className="recipe-body">
        <div>
          <h2>Sastojci</h2>
          <p className="ingredients-text">
            {recipe.ingredients ? (
              recipe.ingredients.split('\n')
                .filter(line => line.trim())
                .map((line, index, array) => (
                  <React.Fragment key={index}>
                    • {line.trim()}
                    {index < array.length - 1 && <br />}
                  </React.Fragment>
                ))
            ) : (
              recipe.ingredients
            )}
          </p>
          
          {/* Fotografije ispod sastojaka */}
          {ingredientsImages.length > 0 && (
            <div className="recipe-images" style={{ marginTop: "20px" }}>
              {ingredientsImages.map((img, index) => (
                <div key={img.id || index} className="recipe-image-wrapper">
                  <img
                    src={img.image_url}
                    alt={recipe.title}
                    className="recipe-image"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2>Priprema</h2>
          <p>{recipe.steps}</p>
          
          {/* Fotografije ispod koraka pripreme */}
          {stepsImages.length > 0 && (
            <div className="recipe-images" style={{ marginTop: "20px" }}>
              {stepsImages.map((img, index) => (
                <div key={img.id || index} className="recipe-image-wrapper">
                  <img
                    src={img.image_url}
                    alt={recipe.title}
                    className="recipe-image"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="recipe-actions">
        <button className="action-btn print-btn" onClick={() => window.print()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <path d="M6 14h12v8H6z" />
          </svg>
          <span>Print</span>
        </button>

        <button 
          className="action-btn pdf-btn" 
          onClick={() => {
            const printWindow = window.open('', '_blank');
            const formatIngredients = (ingredients) => {
              if (!ingredients) return '';
              return ingredients.split('\n')
                .filter(line => line.trim())
                .map(line => `• ${line.trim()}`)
                .join('\n');
            };
            
            const recipeContent = `
              <html>
                <head>
                  <title>${recipe.title}</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                    h1 { color: #1f6074; font-size: 2rem; }
                    h2 { color: #1f6074; margin-top: 30px; }
                    p { line-height: 1.6; white-space: pre-line; }
                  </style>
                </head>
                <body>
                  <h1>${recipe.title}</h1>
                  ${recipe.description ? `<p>${recipe.description}</p>` : ''}
                  <h2>Sastojci</h2>
                  <p>${formatIngredients(recipe.ingredients)}</p>
                  <h2>Priprema</h2>
                  <p>${recipe.steps || ''}</p>
                </body>
              </html>
            `;
            printWindow.document.write(recipeContent);
            printWindow.document.close();
            printWindow.print();
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
            <path d="M10 9H8" />
          </svg>
          <span>PDF</span>
        </button>

        <a 
          className="action-btn email-btn" 
          href={`mailto:?subject=${encodeURIComponent(recipe.title)}&body=${encodeURIComponent(
            `${recipe.title}\n\n${recipe.description || ''}\n\nSastojci:\n${
              recipe.ingredients 
                ? recipe.ingredients.split('\n').filter(line => line.trim()).map(line => `• ${line.trim()}`).join('\n')
                : ''
            }\n\nPriprema:\n${recipe.steps || ''}\n\n${window.location.href}`
          )}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <path d="M22 6l-10 7L2 6" />
          </svg>
          <span>Email</span>
        </a>
      </div>

    </div>
  );
}
